import styled, { EFontSizes, spacing } from "@rentiohq/web-theme";

/**
 * Colors, spacing and font sizes come from the theme. The scene next door keeps
 * a local map of hardcoded hex values; this dialog deliberately does not use it,
 * because those hexes are copies of these same tokens and the copy is the thing
 * that drifts.
 *
 * Four levels, each a different kind of thing rather than a different size of
 * the same thing: the candidate at Medium in full color, the day as a small
 * uppercase divider, the moment at Normal, and everything about a moment at
 * Small in gray. The day is deliberately the quietest of the four: it separates
 * the list, it does not compete with the name of the person the dialog is for.
 */

/**
 * Who this is about. The heading says what happens; this says to whom, and it
 * is the one thing on the dialog the broker must not misread, so it carries a
 * title's weight rather than a caption's.
 */
export const Subtitle = styled.div`
  margin-bottom: ${spacing("extraLoose")};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Medium]}px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral100};
`;

/** Carries the gap between two days, which a heading cannot: every heading is
 * the first child of its own day, so a :first-child rule would match them all. */
export const Day = styled.div`
  & + & {
    margin-top: ${spacing("loose")};
  }
`;

export const DayHeading = styled.div`
  margin: 0 0 ${spacing("tight")};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Small]}px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.neutral70};
`;

/**
 * One bookable moment. The moment the candidate already stands in is rendered
 * with `$isCurrent`: present so the list has no unexplained hole where they are,
 * but muted and unselectable, because moving somebody to where they already are
 * is not a move.
 */
export const Slot = styled.button<{
  $isSelected: boolean;
  $isCurrent: boolean;
}>`
  width: 100%;
  display: block;
  margin-bottom: ${spacing("extraTight")};
  padding: ${spacing("tight")} ${spacing("base")};
  border: 1px solid
    ${({ theme, $isSelected }) =>
      $isSelected
        ? theme.getColor("primary", "800")
        : theme.getColor("gray", "200")};
  border-radius: ${({ theme }) => theme.borderRadius()};
  background: ${({ theme, $isSelected, $isCurrent }) => {
    if ($isSelected) return theme.getColor("primary", "0");

    return $isCurrent ? theme.getColor("gray", "0") : theme.colors.neutral0;
  }};
  cursor: ${({ $isCurrent }) => ($isCurrent ? "default" : "pointer")};
  text-align: left;
`;

export const SlotTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing("base")};
`;

export const SlotTime = styled.span<{ $isCurrent: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Normal]}px;
  font-weight: 600;
  color: ${({ theme, $isCurrent }) =>
    $isCurrent ? theme.getColor("gray", "600") : theme.colors.neutral100};
`;

export const SlotMeta = styled.span`
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Small]}px;
  color: ${({ theme }) => theme.colors.neutral70};
`;

export const SlotNames = styled.div<{ $isCurrent: boolean }>`
  margin-top: ${spacing("extraTight")};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Small]}px;
  color: ${({ theme, $isCurrent }) =>
    $isCurrent ? theme.getColor("gray", "600") : theme.colors.neutral70};
`;

/**
 * The candidate being moved, picked out among the others in a moment. Weight
 * only, no color of its own: it sits on the muted row of the moment they are
 * already in, and a bright name there would read as something to click.
 */
export const OwnName = styled.span`
  font-weight: 700;
`;

/**
 * The confirm button, in the body rather than in the modal's own footer.
 *
 * The footer sits outside the scrolling area, so it measures to the true edge
 * of the dialog while the moments above it lose the scrollbar's width. In here
 * it shares their column and lines up with them, and sticky keeps it in view
 * the way the footer did.
 */
export const StickyActions = styled.div`
  position: sticky;
  /* Pinned to the bottom of the scrolling area, not past it. The negative
     bottom margin cancels the body's own bottom padding, so the bar ends flush
     with the dialog once the list is scrolled to its end. */
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  margin: ${spacing("loose")} 0 -${spacing("extraLoose")};
  padding: ${spacing("base")} 0;
  border-top: 1px solid ${({ theme }) => theme.getColor("gray", "200")};
  background: ${({ theme }) => theme.colors.neutral0};
`;

export const EmptyState = styled.div`
  padding: ${spacing("extraLoose")} 0;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Normal]}px;
  color: ${({ theme }) => theme.colors.neutral70};
`;

export const EmptyAction = styled.div`
  margin-top: ${spacing("base")};
`;

export const CancelIntro = styled.p`
  margin: 0 0 ${spacing("base")};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Medium]}px;
  color: ${({ theme }) => theme.colors.neutral100};
`;

export const ConsequenceList = styled.ul`
  margin: 0;
  padding-left: ${spacing("base")};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Normal]}px;
  color: ${({ theme }) => theme.colors.neutral80};
`;

export const Consequence = styled.li`
  margin-bottom: ${spacing("extraTight")};
`;
